/**
 * DonationModal.jsx
 * Modal for donating to a practitioner directly (no booking required).
 * Hybrid: external payment links (Venmo/CashApp/PayPal) or Stripe.
 * $5 minimum donation.
 */

import { useState, useEffect } from "react";
import { X, Heart, ExternalLink, Loader } from "lucide-react";
import { apiFetch } from "./api";
import StripeInAppCheckout from "../../components/StripeInAppCheckout";
import { isMobileBuild } from "@/lib/mobileBuild";
import UpgradeOnWebNotice from "@/components/UpgradeOnWebNotice";

const styles = `
  .dm-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.5);
    backdrop-filter: blur(4px);
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
  }

  .dm-modal {
    background: #fff;
    border-radius: 16px;
    width: 100%;
    max-width: 440px;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 20px 60px rgba(0,0,0,0.2);
  }

  .dm-header {
    padding: 20px 24px 0;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
  }

  .dm-title {
    font-family: 'Crimson Pro', serif;
    font-size: 22px;
    font-weight: 500;
    color: #1a1a16;
    margin: 0;
  }

  .dm-subtitle {
    font-size: 13px;
    color: #6a6a60;
    margin: 4px 0 0;
  }

  .dm-close {
    background: none;
    border: none;
    color: #9a9a90;
    cursor: pointer;
    padding: 4px;
    border-radius: 50%;
    transition: color 0.15s;
  }
  .dm-close:hover { color: #1a1a16; }

  .dm-body {
    padding: 20px 24px 24px;
  }

  .dm-notice {
    font-size: 12px;
    color: #6a6a60;
    font-style: italic;
    margin: 0 0 16px;
  }

  .dm-external {
    background: #f8f6f2;
    border-radius: 10px;
    padding: 14px;
    margin-bottom: 14px;
  }

  .dm-ext-title {
    font-size: 13px;
    font-weight: 600;
    color: #1a1a16;
    margin: 0 0 8px;
  }

  .dm-ext-links {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .dm-ext-link {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    background: #fff;
    border: 1px solid #e4e2de;
    border-radius: 8px;
    font-size: 13px;
    color: #1b3a2d;
    font-weight: 500;
    text-decoration: none;
    transition: background 0.15s;
  }
  .dm-ext-link:hover { background: #fef9ea; }

  .dm-or {
    text-align: center;
    color: #9a9a90;
    font-size: 12px;
    margin: 14px 0;
    position: relative;
  }
  .dm-or::before, .dm-or::after {
    content: '';
    position: absolute;
    top: 50%;
    width: calc(50% - 20px);
    height: 1px;
    background: #e4e2de;
  }
  .dm-or::before { left: 0; }
  .dm-or::after { right: 0; }

  .dm-amount-wrap {
    position: relative;
    margin-bottom: 12px;
  }

  .dm-dollar {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: #9a9a90;
    font-size: 18px;
    font-weight: 600;
  }

  .dm-amount-input {
    width: 100%;
    padding: 14px 14px 14px 32px;
    border: 1.5px solid #e4e2de;
    border-radius: 10px;
    font-family: 'Outfit', sans-serif;
    font-size: 18px;
    font-weight: 600;
    color: #1a1a16;
    outline: none;
  }
  .dm-amount-input:focus { border-color: #2a5a44; }

  .dm-min {
    font-size: 11px;
    color: #b8734a;
    margin: 0 0 12px;
  }

  .dm-fee {
    font-size: 11px;
    color: #b8734a;
    margin: 8px 0 0;
  }

  .dm-donate-btn {
    width: 100%;
    padding: 14px;
    background: #1b3a2d;
    color: #fff;
    border: none;
    border-radius: 10px;
    font-family: 'Outfit', sans-serif;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: opacity 0.15s;
  }
  .dm-donate-btn:hover { opacity: 0.9; }
  .dm-donate-btn:disabled { opacity: 0.5; cursor: not-allowed; }

  .dm-success {
    text-align: center;
    padding: 16px 0;
  }

  .dm-success-icon {
    width: 52px;
    height: 52px;
    border-radius: 50%;
    background: #e8f5e8;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 14px;
    color: #2a5a44;
  }

  .dm-success h3 {
    font-family: 'Crimson Pro', serif;
    font-size: 20px;
    color: #1a1a16;
    margin: 0 0 6px;
  }

  .dm-success p {
    font-size: 13px;
    color: #6a6a60;
    margin: 0;
  }
`;

export default function DonationModal({ vendorId, practitionerName, onClose }) {
  const [donationInfo, setDonationInfo] = useState(null);
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [donated, setDonated] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [chargedAmount, setChargedAmount] = useState(null);
  const [procFee, setProcFee] = useState(0);
  const [donationId, setDonationId] = useState(null);
  const [payError, setPayError] = useState(null);

  useEffect(() => {
    apiFetch(`/session-feedback/donation-info/${vendorId}`)
      .then(setDonationInfo)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [vendorId]);

  const ext = donationInfo?.external_payment;
  const hasExternal = ext && (ext.venmo_handle || ext.cashapp_handle || ext.paypal_handle);
  const parsedAmount = parseFloat(amount) || 0;

  const submitDonation = async () => {
    if (parsedAmount < 5) return;
    setProcessing(true);
    setPayError(null);
    try {
      const res = await apiFetch("/session-feedback/donations", {
        method: "POST",
        body: JSON.stringify({ vendor_id: vendorId, amount: parsedAmount }),
      });
      setDonationId(res.donation_id);
      setChargedAmount(res.charged_amount ?? parsedAmount);
      setProcFee(res.processing_fee ?? 0);
      if (res.requires_action && res.client_secret) {
        setClientSecret(res.client_secret);
      } else {
        setDonated(true);
      }
    } catch (e) {
      setPayError(e?.message || "We couldn't start that donation. Please try again.");
    }
    setProcessing(false);
  };

  const finishDonation = async () => {
    try {
      await apiFetch(`/session-feedback/donations/${donationId}/confirm`, { method: "POST" });
    } catch (e) {
      // The card cleared; recording the outcome is not worth blocking the thank-you on
    }
    setDonated(true);
  };

  return (
    <>
      <style>{styles}</style>
      <div className="dm-overlay" onClick={onClose}>
        <div className="dm-modal" onClick={e => e.stopPropagation()} data-testid="donation-modal">
          <div className="dm-header">
            <div>
              <h2 className="dm-title">Support {practitionerName || "Practitioner"}</h2>
              <p className="dm-subtitle">Send a donation directly — no booking required.</p>
            </div>
            <button className="dm-close" onClick={onClose} data-testid="donation-close">
              <X size={18} />
            </button>
          </div>

          <div className="dm-body">
            {loading ? (
              <div style={{ textAlign: 'center', padding: 24, color: '#9a9a90' }}>
                <Loader size={20} style={{ animation: 'spin 1s linear infinite' }} />
              </div>
            ) : donated ? (
              <div className="dm-success">
                <div className="dm-success-icon"><Heart size={22} /></div>
                <h3>Thank you!</h3>
                <p>Your donation has been sent to {practitionerName}.</p>
              </div>
            ) : clientSecret ? (
              <StripeInAppCheckout
                clientSecret={clientSecret}
                amount={chargedAmount ?? parsedAmount}
                netAmount={parsedAmount}
                processingFee={procFee}
                label={`Donation to ${practitionerName}`}
                onSuccess={finishDonation}
                onCancel={() => setClientSecret(null)}
              />
            ) : (
              <>
                <p className="dm-notice">100% of your donation goes to the practitioner.</p>

                {hasExternal && (
                  <>
                    <div className="dm-external">
                      <p className="dm-ext-title">Send directly (no processing fees):</p>
                      <div className="dm-ext-links">
                        {ext.venmo_handle && (
                          <a href={`https://venmo.com/${ext.venmo_handle}`} target="_blank" rel="noopener noreferrer" className="dm-ext-link" data-testid="donate-venmo">
                            <ExternalLink size={14} /> Venmo: @{ext.venmo_handle}
                          </a>
                        )}
                        {ext.cashapp_handle && (
                          <a href={`https://cash.app/${ext.cashapp_handle}`} target="_blank" rel="noopener noreferrer" className="dm-ext-link" data-testid="donate-cashapp">
                            <ExternalLink size={14} /> Cash App: {ext.cashapp_handle}
                          </a>
                        )}
                        {ext.paypal_handle && (
                          <a href={`https://paypal.me/${ext.paypal_handle}`} target="_blank" rel="noopener noreferrer" className="dm-ext-link" data-testid="donate-paypal">
                            <ExternalLink size={14} /> PayPal: {ext.paypal_handle}
                          </a>
                        )}
                      </div>
                    </div>
                    <div className="dm-or">or</div>
                  </>
                )}

                <div className="dm-amount-wrap">
                  <span className="dm-dollar">$</span>
                  <input
                    className="dm-amount-input"
                    type="number"
                    min="5"
                    step="0.01"
                    placeholder="0.00"
                    value={amount}
                    onChange={e => setAmount(e.target.value)}
                    data-testid="donation-amount"
                  />
                </div>
                <p className="dm-min">Minimum donation: $5.00</p>

                {isMobileBuild() ? (
                  <div style={{ marginTop: 16 }}>
                    <UpgradeOnWebNotice
                      title="Donate on our website"
                      description="Card donations are handled on our website. Open it there to send your support — or use one of the direct options above."
                      ctaLabel="Donate on Website"
                      path="/dashboard/marketplace/bookings"
                      testId="donation-web-notice"
                      showQr={false}
                    />
                  </div>
                ) : parsedAmount >= 5 && (
                  <>
                    <p className="dm-fee">
                      Card payments add a 3% + $0.30 processing fee, so the practitioner receives the full amount.
                    </p>
                    {payError && (
                      <p className="dm-fee" style={{ color: "#a12b2b" }} data-testid="donation-error">
                        {payError}
                      </p>
                    )}
                    <button
                      className="dm-donate-btn"
                      onClick={submitDonation}
                      disabled={processing}
                      data-testid="submit-donation-btn"
                    >
                      <Heart size={16} />
                      {processing ? "Processing..." : `Donate $${parsedAmount.toFixed(2)} (+ $${(parsedAmount * 0.03 + 0.30).toFixed(2)} processing)`}
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
